import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { authApi } from '@/api/auth'
import { ApiError, bindRequestSession } from '@/api/http'
import { AUTH_SESSION_KEY, AuthStorageError, guestSession, loadAuthSession, readAuthSession, sameSession, writeAuthSession, type StoredAuthSession } from '@/utils/authSession'
import { createRequestId } from '@/utils/requestId'
import type { AuthResponse, LoginRequest, RegisterRequest, User } from '@/types/auth'

export const useAuthStore = defineStore('auth', () => {
  let initial: StoredAuthSession
  let initialError = ''
  try { initial = loadAuthSession() }
  catch (cause) { initial = guestSession(); initialError = cause instanceof Error ? cause.message : '無法讀取登入狀態。' }
  const accessToken = ref(initial.token)
  const user = ref(initial.user)
  const sessionRevision = ref(initial.revision)
  let expiresAt = initial.expiresAt
  let attempt = 0
  const initialized = ref(false)
  const busy = ref(false)
  const sessionNotice = ref(initialError)
  const requiresReload = ref(!!initialError)
  const snapshot = () => ({ token: accessToken.value, revision: sessionRevision.value })
  bindRequestSession(snapshot)

  const isAuthenticated = computed(() => Boolean(!requiresReload.value && accessToken.value && user.value))
  const isAdmin = computed(() => user.value?.role === 'ADMIN')
  const displayName = computed(() => user.value?.displayName || user.value?.username || '訪客')
  const viewKey = computed(() => accessToken.value ? `${user.value?.id ?? 'loading'}:${sessionRevision.value}` : 'guest')

  function apply(session: StoredAuthSession) {
    accessToken.value = session.token
    user.value = session.user
    sessionRevision.value = session.revision
    expiresAt = session.expiresAt
    requiresReload.value = false
    window.dispatchEvent(new Event('auth:session-updated'))
  }

  function isCurrentSession(revision: string) {
    try { return !requiresReload.value && revision === sessionRevision.value && sameSession(snapshot(), readAuthSession()) }
    catch { return false }
  }

  function changedError() {
    return new ApiError({ code: 'AUTH_SESSION_CHANGED', message: '登入帳號已變更，請重新操作' }, { requestSent: false })
  }

  function failStorage(cause: unknown): never {
    attempt++
    accessToken.value = null
    user.value = null
    sessionRevision.value = createRequestId()
    requiresReload.value = true
    initialized.value = true
    busy.value = false
    const storageError = cause instanceof AuthStorageError ? cause : new AuthStorageError()
    sessionNotice.value = storageError.message
    throw new ApiError({ code: storageError.code, message: storageError.message }, { requestSent: false })
  }

  /** Invalidate even a hidden page immediately; rendering can wait until it becomes visible. */
  function checkSharedSession(): boolean {
    if (isCurrentSession(sessionRevision.value)) {
      const stored = readAuthSession()
      if (stored?.user?.id === user.value?.id) user.value = stored?.user ?? null
      return false
    }
    if (!requiresReload.value) {
      attempt++
      accessToken.value = null
      user.value = null
      sessionRevision.value = createRequestId()
      initialized.value = true
      busy.value = false
      requiresReload.value = true
      sessionNotice.value = '登入狀態已變更，請重新整理頁面。'
    }
    return true
  }

  function clear() {
    const ownsStoredSession = isCurrentSession(sessionRevision.value)
    attempt++
    busy.value = false
    initialized.value = true
    const guest = guestSession()
    // Clear memory first, including on a storage failure or a stale tab.
    accessToken.value = null
    user.value = null
    sessionRevision.value = guest.revision
    expiresAt = null
    if (!ownsStoredSession) { requiresReload.value = true; return }
    try { writeAuthSession(guest); apply(guest) }
    catch (cause) { try { failStorage(cause) } catch { /* notice remains visible; private requests are blocked */ } }
  }

  async function authenticate(request: () => Promise<AuthResponse>) {
    if (!isCurrentSession(sessionRevision.value)) throw changedError()
    const current: StoredAuthSession = { version: 1, ...snapshot(), user: user.value, expiresAt, revision: createRequestId() }
    try { writeAuthSession(current); apply(current) } catch (cause) { return failStorage(cause) }
    const requestAttempt = ++attempt
    const requestedRevision = current.revision
    busy.value = true
    sessionNotice.value = ''
    try {
      const result = await request()
      if (attempt !== requestAttempt || !isCurrentSession(requestedRevision)) throw changedError()
      if (typeof result.accessToken !== 'string' || !result.accessToken || typeof result.expiresAt !== 'string'
        || typeof result.user?.id !== 'string' || !result.user.id || typeof result.user.username !== 'string'
        || !['USER', 'ADMIN'].includes(result.user.role)) {
        throw new ApiError({ code: 'AUTH_INVALID_TOKEN', message: '登入資料不完整，請重新登入。' })
      }
      const next: StoredAuthSession = { version: 1, token: result.accessToken, user: result.user, expiresAt: result.expiresAt, revision: createRequestId() }
      try { writeAuthSession(next); apply(next) } catch (cause) { return failStorage(cause) }
      initialized.value = true
      return result.user
    } finally {
      if (attempt === requestAttempt) busy.value = false
    }
  }

  const login = (payload: LoginRequest) => authenticate(() => authApi.login(payload))
  const loginAdmin = (payload: LoginRequest) => authenticate(() => authApi.adminLogin(payload))
  const register = (payload: RegisterRequest) => authenticate(() => authApi.register(payload))

  async function loadProfile(request: () => Promise<User>) {
    const revision = sessionRevision.value
    const owner = user.value?.id
    if (!isAuthenticated.value || !owner || !isCurrentSession(revision)) throw changedError()
    if (!navigator.locks?.request) {
      throw new ApiError({ code: 'PROFILE_LOCK_UNAVAILABLE', message: '目前瀏覽器無法更新個人資料，請使用支援安全連線的瀏覽器。' })
    }
    return navigator.locks.request(`${AUTH_SESSION_KEY}.profile`, { mode: 'exclusive' }, async () => {
      if (!isCurrentSession(revision) || user.value?.id !== owner) throw changedError()
      const result = await request()
      if (!isCurrentSession(revision) || user.value?.id !== owner) throw changedError()
      if (result.id !== owner || result.username !== user.value.username || !['USER', 'ADMIN'].includes(result.role)) {
        throw new ApiError({ code: 'PROFILE_INVALID_RESPONSE', message: '個人資料讀取失敗，請重新整理。' })
      }
      const current = readAuthSession()
      if (!current || current.user?.id !== owner) throw changedError()
      try { writeAuthSession({ ...current, user: result }); user.value = result }
      catch (cause) { return failStorage(cause) }
      return result
    })
  }

  const refreshProfile = () => loadProfile(() => authApi.me())
  const updateProfile = (payload: { displayName: string }) => {
    const displayName = payload.displayName.trim()
    return loadProfile(() => authApi.updateProfile({ displayName }))
  }

  async function hydrate() {
    if (initialized.value) return
    if (!accessToken.value) { initialized.value = true; return }
    const requestedRevision = sessionRevision.value
    try {
      const currentUser = await authApi.me()
      if (!isCurrentSession(requestedRevision)) return
      const current = readAuthSession()
      if (!current) return
      try { writeAuthSession({ ...current, user: currentUser }); user.value = currentUser }
      catch (cause) { failStorage(cause) }
    } catch {
      if (isCurrentSession(requestedRevision)) clear()
    } finally {
      if (sessionRevision.value === requestedRevision || !accessToken.value) initialized.value = true
    }
  }

  async function logout() {
    const token = accessToken.value
    // This transport uses only the captured credential, so local clear cannot cancel server revocation.
    const request = token ? authApi.logout(token) : Promise.resolve()
    clear()
    const logoutRevision = sessionRevision.value
    if (isCurrentSession(logoutRevision) && token) sessionNotice.value = '正在結束工作階段…'
    let confirmed = true
    try { await request } catch { confirmed = false }
    if (isCurrentSession(logoutRevision)) {
      sessionNotice.value = confirmed ? '' : '已退出此裝置，伺服器工作階段未確認失效。'
    }
    return confirmed
  }

  async function changePassword(payload: { currentPassword: string; newPassword: string }) {
    const revision = sessionRevision.value
    if (!isAuthenticated.value || !isCurrentSession(revision)) throw changedError()
    await authApi.changePassword(payload)
    if (!isCurrentSession(revision)) throw changedError()
    clear()
  }

  return { accessToken, user, sessionRevision, initialized, busy, sessionNotice, requiresReload, viewKey,
    isAuthenticated, isAdmin, displayName, login, loginAdmin, register, hydrate, logout, clear,
    isCurrentSession, checkSharedSession, refreshProfile, updateProfile, changePassword }
})
